import { useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import ChangePasswordModal from '../GlobalComponents/ChangePasswordModal';
import {
    FaFileInvoiceDollar,
    FaTag,
    FaBuilding,
    FaChartLine,
    FaUserShield,
    FaUserMd,
    FaUsers,
    FaFileMedicalAlt,
    FaHandshake,
    FaBriefcaseMedical,
    FaBars,
    FaFileAlt,
    FaSignOutAlt,
    FaKey,
    FaBell,
    FaStore,
} from 'react-icons/fa';
import apiClient from '../../utils/axiosPetition';

import SvgIcon from '../../utils/Icon';
import salutioLogo from '../../images/salutioRed.png'; // Logo
import { useDataUser } from '../../context/dataUserContext';

const NavbarClinicAdmin = () => {
    const viewActive = useLocation();
    const [selected, setSelected] = useState(viewActive.pathname.split('/')[2]);
    // false = barra estrecha (iconos); true = sidebar expandido fijo
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [menuLateralItems, setMenuLateralItems] = useState([]);
    const [showModalChangePassword, setShowModalChangePassword] = useState(false);
    const [doctorsInReview, setDoctorsInReview] = useState(0);
    const navigate = useNavigate();
    const { setIsAuth, clinicId } = useDataUser();
    const logo = localStorage.getItem('imageProfile');

    // Función para obtener doctores en revisión
    const fetchDoctorsInReview = async () => {
        if (!clinicId) return;
        
        try {
            const response = await apiClient.get(`/api/clinic/getDoctors/${clinicId}`);
            const doctors = response.data || [];
            
            // Contar doctores con estado "en_revision"
            const inReviewCount = doctors.filter(doctor => doctor.estado === 'en_revision').length;
            setDoctorsInReview(inReviewCount);
        } catch (error) {
            console.error('Error fetching doctors in review:', error);
            setDoctorsInReview(0);
        }
    };

    useEffect(() => {
        setMenuLateralItems([
            { id: "info-clinica", icon: FaBuilding, text: "Perfil de la clínica", path: "info-clinica" },
            { id: "tablero-control", icon: FaChartLine, text: "Tablero de control", path: "tablero-control" },
            { id: "administradores", icon: FaUserShield, text: "Administradores", path: "administradores" },
            { id: "afiliados", icon: FaUserMd, text: "Profesionales", path: "afiliados" },
            { id: "usuarios", icon: FaUsers, text: "Pacientes", path: "usuarios" },
            { id: "documentos-pacientes", icon: FaFileAlt, text: "Documentos médicos", path: "documentos-pacientes" },
            { id: "rips", icon: FaFileMedicalAlt, text: "RIPS", path: "rips" },
            { id: "facturacion", icon: FaFileInvoiceDollar, text: "Facturación", path: "facturacion" },
            { id: "gestion-precios", icon: FaTag, text: "Gestión de Precios", path: "gestion-precios" },
            { id: "profesiones", icon: FaBriefcaseMedical, text: "Profesiones", path: "profesiones" },
            { id: "marketplace", icon: FaStore, text: "REDPLIKAPLACE", path: "marketplace" },
            { id: "aliados", icon: FaHandshake, text: "Aliados", path: "aliados" },
        ]);

        // Cargar doctores en revisión al montar el componente
        fetchDoctorsInReview();
    }, [clinicId]);

    // Actualizar contador cada 30 segundos
    useEffect(() => {
        const interval = setInterval(() => {
            fetchDoctorsInReview();
        }, 30000); // 30 segundos

        return () => clearInterval(interval);
    }, [clinicId]);

    const toogleLink = (ev) => {
        const { id } = ev.target;

        if (window.innerWidth < 1024) {
            setIsSidebarOpen(false);
        }
        setSelected(id);

        // Actualizar contador cuando se navega a afiliados
        if (id === 'afiliados') {
            fetchDoctorsInReview();
        }
    }

    const handleLogout = () => {
        localStorage.clear();
        setIsAuth(false);
        navigate('/login');
    };

    return (
        <div className="flex h-screen max-h-screen overflow-hidden bg-[#f0f0f0]">
            {/* Overlay para móviles */}
            {isSidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-[55] lg:hidden transition-opacity duration-300 backdrop-blur-sm"
                    onClick={() => setIsSidebarOpen(false)}
                />
            )}

            {/* Sidebar fijo: cerrado = rail de iconos; abierto = panel completo */}
            <aside
                className={`
                    flex flex-col overflow-hidden
                    fixed top-0 left-0 h-screen bg-[#205087ff] shadow-xl z-[60]
                    transition-all duration-300 ease-in-out
                    ${isSidebarOpen ? 'w-64 max-w-[16rem] translate-x-0' : 'w-20 max-w-[5rem] -translate-x-full lg:translate-x-0'}
                `}
            >
                {/* Cabecera compacta */}
                <div className={`flex items-center shrink-0 border-b border-white/10 bg-gradient-to-b from-[#1f94c2ff] to-[#205087ff] transition-all duration-300 ${isSidebarOpen ? 'gap-2.5 py-2.5 px-3' : 'justify-center py-2.5 px-0 lg:py-3'}`}>
                    <div className={`rounded-full ring-2 ring-white/30 flex-shrink-0 bg-white overflow-hidden flex items-center justify-center transition-all duration-300 ${isSidebarOpen ? 'w-10 h-10' : 'w-10 h-10 lg:w-11 lg:h-11'}`}>
                        {logo ? (
                            <img src={logo} alt="Logo" className="w-full h-full object-cover" />
                        ) : (
                            <FaBuilding className="w-5 h-5 text-[#205087ff]" />
                        )}
                    </div>
                    {isSidebarOpen && (
                        <div className="min-w-0 flex-1">
                            <p className="text-white font-semibold text-sm truncate">Administración</p>
                        </div>
                    )}
                </div>

                {/* Lista de opciones */}
                <nav className="flex-1 min-h-0 py-2 overflow-y-auto overflow-x-hidden">
                    <ul className="space-y-1 px-2">
                        {menuLateralItems && menuLateralItems.map((link) => (
                            <li key={link.id}>
                                <Link
                                    id={link.id}
                                    to={link.path}
                                    onClick={toogleLink}
                                    title={!isSidebarOpen ? link.text : undefined}
                                    className={`
                                        flex items-center w-full rounded-lg text-white font-medium
                                        transition-all duration-200
                                        hover:bg-[#f79865ff] hover:text-white
                                        ${isSidebarOpen ? 'gap-3 px-3 py-2' : 'justify-center px-0 py-2'}
                                        ${selected === link.id 
                                            ? 'bg-[#f79865ff] text-white font-semibold' 
                                            : 'hover:bg-white/10'}
                                    `}
                                >
                                    <div className="relative flex items-center justify-center w-8 h-8 shrink-0 rounded-lg bg-white/10">
                                        <SvgIcon
                                            Icon={typeof link.icon === 'function' ? link.icon : link.icon}
                                            className="w-4 h-4 text-white"
                                        />
                                        {link.id === 'afiliados' && doctorsInReview > 0 && (
                                            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                                                {doctorsInReview > 9 ? '9+' : doctorsInReview}
                                            </span>
                                        )}
                                    </div>
                                    {isSidebarOpen && (
                                        <>
                                            <span className="flex-1 truncate text-sm">{link.text}</span>
                                            {link.id === 'afiliados' && doctorsInReview > 0 && (
                                                <span className="text-xs bg-red-500/90 text-white px-2 py-0.5 rounded font-medium shrink-0">
                                                    {doctorsInReview}
                                                </span>
                                            )}
                                        </>
                                    )}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </nav>

                {/* Pie compacto */}
                <div className={`border-t border-white/10 shrink-0 ${isSidebarOpen ? 'px-3 py-2' : 'py-2 px-0 flex justify-center'}`}>
                    <p className={`text-white/60 text-xs text-center truncate transition-opacity duration-200 ${isSidebarOpen ? 'opacity-100' : 'opacity-0 lg:opacity-0 lg:max-w-0 lg:overflow-hidden lg:h-0 lg:py-0'}`}>
                        Redplika©
                    </p>
                </div>
            </aside>

            {/* Contenido principal */}
            <section className={`relative flex flex-col flex-1 min-h-0 overflow-y-auto transition-all duration-300 ${isSidebarOpen ? 'lg:ml-64' : 'lg:ml-20'}`}>

                <nav className="sticky top-0 bg-white px-3 py-2 z-[9] shadow-sm border-b border-slate-200 shrink-0">
                    <ul className="flex w-full items-center justify-between">

                        <li className="flex items-center gap-3">
                            <button
                                type="button"
                                className="bg-[#1f94c2ff] p-2 rounded-md cursor-pointer hover:bg-[#205087] transition-colors"
                                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                                aria-label="Menú"
                            >
                                <FaBars
                                    className="w-5 h-5 text-white"
                                />
                            </button>
                            <img
                                src={salutioLogo}
                                alt="Logo"
                                className="h-7 md:h-8 w-auto object-contain"
                            />
                        </li>

                        <li className="flex items-center gap-2 md:gap-4 text-sm">
                            {/* Notificación de doctores en revisión */}
                            {doctorsInReview > 0 && (
                                <div className="relative group">
                                    <Link to="afiliados">
                                        <FaBell className="text-red-500 w-5 h-5 md:w-6 md:h-6 cursor-pointer transform hover:scale-110 shrink-0 animate-pulse" />
                                    </Link>
                                    <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 md:w-5 md:h-5 flex items-center justify-center font-bold text-[10px] md:text-xs">
                                        {doctorsInReview > 9 ? '9+' : doctorsInReview}
                                    </div>
                                    <span className="absolute left-1/2 -translate-x-1/2 top-full mt-2 w-max bg-red-500 text-white text-xs rounded p-1 opacity-0 group-hover:opacity-100 transition whitespace-nowrap z-20">
                                        {doctorsInReview} doctor{doctorsInReview > 1 ? 'es' : ''} en revisión
                                    </span>
                                </div>
                            )}

                            <div className="relative group">
                                <FaKey
                                    className="text-[#205087ff] w-5 h-5 md:w-6 md:h-6 cursor-pointer transform hover:scale-110 shrink-0"
                                    onClick={() => setShowModalChangePassword(!showModalChangePassword)}
                                />
                                <span className="absolute left-1/2 -translate-x-1/2 top-full mt-2 w-max bg-[#1f94c2ff] text-white text-xs rounded p-1 opacity-0 group-hover:opacity-100 transition whitespace-nowrap z-20">
                                    Cambiar contraseña
                                </span>
                            </div>

                            <span className="border-r border-2 border-[#f79865ff] h-8 md:h-10" />

                            <div className="flex items-center cursor-pointer transform hover:scale-110" onClick={handleLogout}>
                                <FaSignOutAlt className="text-[#205087ff] w-5 h-5 md:w-6 md:h-6 mr-1 md:mr-2 shrink-0" />
                                <span className="text-[#1f94c2ff] hidden md:flex text-sm md:text-md"> Salir </span>
                            </div>
                        </li>

                    </ul>
                </nav>
                {/* CONTENIDO PRINCIPAL DEBAJO DEL NAVBAR */}
                <main id="principal-content">
                    <div className="flex flex-col w-full px-4 py-4 md:px-5 md:py-5">
                        <Outlet />
                    </div>
                </main>
            </section>
            {/* MODAL CAMBIO DE CONTRASEÑA */}
            <ChangePasswordModal showModalChangePassword={showModalChangePassword} setShowModalChangePassword={setShowModalChangePassword} />
        </div>
    );
}

export default NavbarClinicAdmin;
